import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type Firestore,
  type Unsubscribe,
} from "firebase/firestore";
import { httpsCallable, type Functions } from "firebase/functions";
import type {
  AppNotification,
  GroupInvitation,
  GroupMember,
} from "@/types/domain";
import {
  invitationSchema,
  roleUpdateSchema,
  type InvitationInput,
} from "@/lib/validation/schemas";

const replayProtected = { limitedUseAppCheckTokens: true } as const;

export async function listMembers(db: Firestore, groupId: string) {
  const snapshot = await getDocs(
    query(
      collection(db, "groups", groupId, "members"),
      where("status", "==", "active"),
      orderBy("joinedAt"),
    ),
  );
  return snapshot.docs.map((item) => item.data() as GroupMember);
}

export async function inviteMember(
  functions: Functions,
  groupId: string,
  input: InvitationInput,
) {
  const parsed = invitationSchema.parse(input);
  await httpsCallable(
    functions,
    "inviteMember",
    replayProtected,
  )({ groupId, ...parsed });
}

export async function respondToInvitation(
  functions: Functions,
  invitationId: string,
  response: "accepted" | "rejected",
) {
  await httpsCallable(
    functions,
    "respondToInvitation",
    replayProtected,
  )({ invitationId, response });
}

export async function revokeInvitation(
  functions: Functions,
  invitationId: string,
) {
  await httpsCallable(
    functions,
    "revokeInvitation",
    replayProtected,
  )({ invitationId });
}

export async function updateMemberRole(
  functions: Functions,
  groupId: string,
  userId: string,
  role: "admin" | "editor" | "viewer",
) {
  const input = roleUpdateSchema.parse({ userId, role });
  await httpsCallable(
    functions,
    "updateMemberRole",
    replayProtected,
  )({ groupId, ...input });
}

export async function removeMember(
  functions: Functions,
  groupId: string,
  userId: string,
) {
  await httpsCallable(
    functions,
    "removeMember",
    replayProtected,
  )({ groupId, userId });
}

export async function transferOwnership(
  functions: Functions,
  groupId: string,
  newOwnerId: string,
) {
  await httpsCallable(functions, "transferGroupOwnership", replayProtected)({
    groupId,
    newOwnerId,
  });
}

export async function listGroupInvitations(db: Firestore, groupId: string) {
  const snapshot = await getDocs(
    query(
      collection(db, "invitations"),
      where("groupId", "==", groupId),
      orderBy("createdAt", "desc"),
      limit(100),
    ),
  );
  return snapshot.docs.map((item) => item.data() as GroupInvitation);
}

export async function listUserInvitations(db: Firestore, email: string) {
  const snapshot = await getDocs(userInvitationsQuery(db, email));
  return snapshot.docs.map((item) => item.data() as GroupInvitation);
}

export function subscribeToUserInvitations(
  db: Firestore,
  email: string,
  onInvitations: (invitations: GroupInvitation[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(
    userInvitationsQuery(db, email),
    (snapshot) =>
      onInvitations(
        snapshot.docs.map((item) => item.data() as GroupInvitation),
      ),
    onError,
  );
}

function userInvitationsQuery(db: Firestore, email: string) {
  return query(
    collection(db, "invitations"),
    where("invitedEmailNormalized", "==", email.toLocaleLowerCase()),
    where("status", "==", "pending"),
    orderBy("createdAt", "desc"),
  );
}

export async function listNotifications(db: Firestore, userId: string) {
  const snapshot = await getDocs(
    query(
      collection(db, "users", userId, "notifications"),
      orderBy("createdAt", "desc"),
      limit(30),
    ),
  );
  return snapshot.docs.map((item) => item.data() as AppNotification);
}
